import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, LogOut, Menu, X, Heart, Bell, Check, Trophy, Target } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Live badge color
const LIVE_RED = '#FF3B3B';

const formatKickoff = (isoString) => {
  try {
    const dt = new Date(isoString);
    const now = new Date();
    const isToday = dt.toDateString() === now.toDateString();
    const timeStr = dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return isToday ? `Today ${timeStr}` : `${dt.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${timeStr}`;
  } catch {
    return isoString;
  }
};

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [hasLiveMatches, setHasLiveMatches] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const [bellTab, setBellTab] = useState('all'); // 'all' | 'reminders'
  const bellRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close bell dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setBellOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchReminders = useCallback(async (currentUser) => {
    if (!currentUser) return;
    const token = localStorage.getItem('token');
    try {
      const [remRes, notifRes] = await Promise.all([
        axios.get(`${API}/reminders`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }),
        axios.get(`${API}/notifications`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }),
      ]);
      const now = new Date();
      const upcoming = (remRes.data.reminders || []).filter(
        (r) => r.match_time && new Date(r.match_time) > now
      );
      setReminders(upcoming);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(notifRes.data.unread_count || 0);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    checkAuthentication();
    checkLiveMatches();

    const liveInterval = setInterval(checkLiveMatches, 30000);
    return () => clearInterval(liveInterval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll reminders every 60 seconds once user is known
  useEffect(() => {
    if (!user) return;
    fetchReminders(user);
    const interval = setInterval(() => fetchReminders(user), 60000);
    return () => clearInterval(interval);
  }, [user, fetchReminders]);

  const checkAuthentication = async () => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (storedUser) setUser(JSON.parse(storedUser));

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.data) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    } catch (error) {
      console.log('Auth check failed:', error.response?.status);
      if (!storedUser) setUser(null);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const checkLiveMatches = async () => {
    try {
      const response = await axios.get(`${API}/content/featured`);
      setHasLiveMatches(response.data.filter(c => c.is_live).length > 0);
    } catch {}
  };

  const handleLogout = async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setReminders([]);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.error('Logout error:', err);
    }
    navigate('/');
  };

  const removeReminder = async (matchId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/reminders/${matchId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setReminders(prev => prev.filter(r => r.match_id !== matchId));
    } catch (e) {
      console.error('Failed to remove reminder:', e);
    }
  };

  const markAllRead = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const isActiveRoute = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { label: 'Home', path: '/', testId: 'nav-home' },
    { label: 'Match Feed', path: '/feed', testId: 'nav-feed' },
    { label: 'Calendar', path: '/calendar', testId: 'nav-calendar' },
    { label: 'Leagues', path: '/competitions', testId: 'nav-leagues' },
    { label: 'Highlights', path: '/highlights', testId: 'nav-highlights' },
    { label: 'Following', path: '/following', testId: 'nav-following', requiresAuth: true },
    { label: 'About', path: '/about', testId: 'nav-about' },
  ];

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-[#0B0B0B]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group" data-testid="logo-link">
            <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shrink-0">
              <Play className="w-6 h-6 text-white fill-white" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-bold text-xl tracking-wider text-white">AFROSPORTS</span>
              <span
                className="text-[12px] md:text-[13px] tracking-[0.03em] text-white/70 font-normal mt-[2px] leading-[1.1]"
                data-testid="brand-tagline"
              >
                The Home of African Sport
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center" role="navigation" aria-label="Main navigation">
            <ul className="flex items-center space-x-1">
              {navItems.map((item) => {
                if (item.requiresAuth && !user) return null;
                const isActive = isActiveRoute(item.path);
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`relative px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors duration-200 flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] ${
                        isActive ? 'text-white' : 'text-white/70 hover:text-white'
                      }`}
                      data-testid={item.testId}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {item.isLive && hasLiveMatches && (
                        <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: LIVE_RED }} />
                      )}
                      {item.label === 'Following' && <Heart className="w-4 h-4" />}
                      <span>{item.label}</span>
                      {isActive && (
                        <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-emerald-500 rounded-full" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Auth + Bell area */}
          <div className="hidden md:flex items-center space-x-3">
            {!isCheckingAuth && (
              <>
                {user ? (
                  <>
                    {/* Notification Bell */}
                    <div className="relative" ref={bellRef}>
                      <button
                        onClick={() => setBellOpen(o => !o)}
                        className="relative p-2 text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
                        data-testid="notification-bell"
                        aria-label={`Notifications${(unreadCount + reminders.length) > 0 ? ` (${unreadCount + reminders.length})` : ''}`}
                      >
                        <Bell className="w-5 h-5" />
                        {(unreadCount + reminders.length) > 0 && (
                          <span
                            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-emerald-500 rounded-full border-2 border-[#0B0B0B] flex items-center justify-center text-[9px] font-bold text-white"
                            data-testid="bell-count"
                          >
                            {unreadCount + reminders.length}
                          </span>
                        )}
                      </button>

                      {/* Notification Dropdown */}
                      {bellOpen && (
                        <div
                          className="absolute right-0 top-full mt-2 w-96 bg-[#1a1a1a] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
                          data-testid="notification-dropdown"
                        >
                          {/* Header with tabs */}
                          <div className="px-4 py-3 border-b border-white/10">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-white font-bold text-sm uppercase tracking-wide">Notifications</h3>
                              {unreadCount > 0 && (
                                <button
                                  onClick={markAllRead}
                                  className="text-emerald-400 text-[10px] font-semibold hover:text-emerald-300 flex items-center gap-1"
                                  data-testid="mark-all-read"
                                >
                                  <Check className="w-3 h-3" /> Mark all read
                                </button>
                              )}
                            </div>
                            <div className="flex gap-1">
                              {[
                                { id: 'all', label: 'All', count: notifications.length },
                                { id: 'reminders', label: 'Reminders', count: reminders.length },
                              ].map(tab => (
                                <button
                                  key={tab.id}
                                  onClick={() => setBellTab(tab.id)}
                                  className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                    bellTab === tab.id
                                      ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                      : 'text-white/50 hover:text-white/70 border border-transparent'
                                  }`}
                                  data-testid={`bell-tab-${tab.id}`}
                                >
                                  {tab.label} {tab.count > 0 && <span className="ml-1 text-[9px]">({tab.count})</span>}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="max-h-80 overflow-y-auto">
                            {/* Notifications Tab */}
                            {bellTab === 'all' && (
                              notifications.length === 0 ? (
                                <div className="px-4 py-8 text-center">
                                  <Bell className="w-8 h-8 text-white/20 mx-auto mb-2" />
                                  <p className="text-white/50 text-sm">No notifications yet</p>
                                  <p className="text-white/30 text-xs mt-1">Follow teams to get match updates</p>
                                </div>
                              ) : (
                                <ul className="divide-y divide-white/5">
                                  {notifications.map((n) => (
                                    <li key={n.notification_id} data-testid={`notif-${n.notification_id}`}>
                                      <Link
                                        to={n.link || (n.match_id ? `/matchday/${n.match_id}` : '/')}
                                        onClick={() => setBellOpen(false)}
                                        className={`flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition-colors ${
                                          !n.read ? 'bg-emerald-500/[0.03]' : ''
                                        }`}
                                      >
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                                          n.type === 'match_result' ? 'bg-emerald-500/15' :
                                          n.type === 'team_goal' ? 'bg-yellow-500/15' :
                                          'bg-white/10'
                                        }`}>
                                          {n.type === 'match_result' ? <Trophy className="w-4 h-4 text-emerald-400" /> :
                                           n.type === 'team_goal' ? <Target className="w-4 h-4 text-yellow-400" /> :
                                           <Bell className="w-4 h-4 text-white/50" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm font-medium truncate ${!n.read ? 'text-white' : 'text-white/70'}`}>{n.title}</p>
                                          <p className="text-white/40 text-xs mt-0.5 line-clamp-2">{n.message}</p>
                                          <p className="text-white/25 text-[10px] mt-1">
                                            {new Date(n.created_at).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                          </p>
                                        </div>
                                        {!n.read && <span className="w-2 h-2 rounded-full bg-emerald-500 mt-2 shrink-0" />}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )
                            )}

                            {/* Reminders Tab */}
                            {bellTab === 'reminders' && (
                              reminders.length === 0 ? (
                                <div className="px-4 py-8 text-center">
                                  <Bell className="w-8 h-8 text-white/20 mx-auto mb-2" />
                                  <p className="text-white/50 text-sm">No upcoming reminders</p>
                                  <p className="text-white/30 text-xs mt-1">Visit a match page to set one</p>
                                </div>
                              ) : (
                                <ul className="divide-y divide-white/5">
                                  {reminders.map((r) => (
                                    <li key={r.reminder_id} className="px-4 py-3 hover:bg-white/5 transition-colors" data-testid={`reminder-item-${r.match_id}`}>
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-white font-semibold text-sm truncate">
                                            {r.teams || r.match_title}
                                          </p>
                                          <p className="text-white/50 text-xs mt-0.5">
                                            {r.competition && <span>{r.competition} · </span>}
                                            <span className="text-emerald-400">{formatKickoff(r.match_time)}</span>
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                          <Link
                                            to={`/match/${r.match_id}`}
                                            onClick={() => setBellOpen(false)}
                                            className="px-3 py-1 bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-500/40 text-emerald-400 text-xs font-semibold rounded-full transition-colors"
                                            data-testid={`reminder-watch-${r.match_id}`}
                                          >
                                            Watch
                                          </Link>
                                          <button
                                            onClick={() => removeReminder(r.match_id)}
                                            className="p-1 text-white/30 hover:text-red-400 transition-colors rounded"
                                            aria-label="Remove reminder"
                                            data-testid={`reminder-remove-${r.match_id}`}
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLogout}
                      className="text-white/70 hover:text-white"
                      data-testid="logout-button"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" data-testid="login-link">
                      <Button variant="ghost" size="sm" className="text-white/70 hover:text-white">
                        Login
                      </Button>
                    </Link>
                    <Link to="/signup" data-testid="signup-link">
                      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full px-6" data-testid="signup-button">
                        Sign Up
                      </Button>
                    </Link>
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10 bg-[#0a0a0a]/95 backdrop-blur-md">
            <nav aria-label="Mobile navigation">
              <ul className="flex flex-col space-y-1">
                {navItems.map((item) => {
                  if (item.requiresAuth && !user) return null;
                  const isActive = isActiveRoute(item.path);
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        className={`block px-4 py-3 text-sm font-semibold uppercase tracking-wide transition-colors duration-200 flex items-center gap-3 focus:outline-none focus-visible:bg-white/10 ${
                          isActive
                            ? 'text-white bg-emerald-500/10 border-l-2 border-emerald-500'
                            : 'text-white/70 hover:text-white hover:bg-white/5'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                        data-testid={`${item.testId}-mobile`}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {item.isLive && hasLiveMatches && (
                          <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: LIVE_RED }} />
                        )}
                        {item.label === 'Following' && <Heart className="w-4 h-4" />}
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}

                {/* Mobile Reminders section */}
                {user && reminders.length > 0 && (
                  <li>
                    <div className="px-4 py-3 border-t border-white/10 mt-2">
                      <p className="text-white/50 text-xs uppercase tracking-wide mb-2 flex items-center gap-2">
                        <Bell className="w-3 h-3" /> Upcoming Reminders
                      </p>
                      <div className="space-y-2">
                        {reminders.map(r => (
                          <div key={r.reminder_id} className="flex items-center justify-between">
                            <div>
                              <p className="text-white text-sm font-medium truncate max-w-[180px]">{r.teams || r.match_title}</p>
                              <p className="text-emerald-400 text-xs">{formatKickoff(r.match_time)}</p>
                            </div>
                            <div className="flex gap-2">
                              <Link
                                to={`/match/${r.match_id}`}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="text-emerald-400 text-xs font-semibold"
                              >
                                Watch
                              </Link>
                              <button onClick={() => removeReminder(r.match_id)} className="text-white/30 hover:text-red-400">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </nav>

            <div className="pt-4 mt-4 border-t border-white/10 px-4">
              {user ? (
                <button
                  onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                  className="w-full text-left text-white/70 hover:text-white transition-colors py-3 flex items-center gap-3 text-sm font-semibold uppercase tracking-wide"
                  data-testid="logout-button-mobile"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              ) : (
                <div className="flex flex-col space-y-2">
                  <Link
                    to="/login"
                    className="block text-white/70 hover:text-white transition-colors py-3 text-sm font-semibold uppercase tracking-wide"
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid="login-link-mobile"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="block bg-emerald-600 hover:bg-emerald-700 text-white text-center py-3 rounded-full text-sm font-bold uppercase tracking-wide"
                    onClick={() => setIsMobileMenuOpen(false)}
                    data-testid="signup-link-mobile"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
